import type { Dayjs } from "dayjs";
import type { VenuePageTimezones } from "@punchup/data/models/venue_pages";
import dayjs from "@punchup/utils/dayjs";
import { localStartsOfToday } from "../showDayWindow";

/*
 * Lower bounds for a show group's "upcoming" shows, anchored to the start of
 * *today* in the venue's local timezone(s). Used by the group drill-down page
 * (`getVenueShowGroupData`) and venue-page URL resolution. The venue page's
 * own grids (grouped and individual) use the tighter `publicShowStartCutoff`
 * grace window from `@punchup/data/server` instead.
 *
 * Why start-of-today: a rolling lookback (`now - 24h`) keeps *yesterday's*
 * late shows visible for most of the next day (a 10pm show lingers until ~10pm
 * tonight). Anchoring to local midnight drops them the moment the venue's
 * calendar day rolls over, while still keeping shows earlier *today* that have
 * already ended.
 *
 * Why the legacy fallback: `venue_pages.timezones` can legitimately be empty
 * (the recompute skips locations without coordinates). Without tz data,
 * "local midnight" is unknowable — and guessing UTC would HIDE a US venue's
 * still-upcoming evening shows from ~8pm ET (00:00 UTC) onward. The legacy
 * rolling `now - 24h` window errs toward staleness, never toward hiding a
 * future show, so it remains the no-data default.
 *
 * `localStartsOfToday` — and the reason the earliest across locations is the
 * safe one — live in [`showDayWindow`](../showDayWindow.ts), shared with the
 * dashboard's Box Office day list.
 */

/**
 * Naive wall-clock form, for filtering `comedian_shows.datetime` — a
 * `timestamp without time zone` column that stores venue-local digits with no
 * offset. Must be rendered with `.format(...)`, never `.toISOString()`: UTC
 * rendering would skew the boundary by the venue's offset (~4-5h for US
 * venues). Falls back to the legacy rolling `now - 24h` (UTC ISO) when the
 * venue has no timezone data.
 */
export function upcomingShowsCutoff(
  venueTimezones: VenuePageTimezones
): string {
  if (!venueTimezones?.length) {
    return dayjs().subtract(1, "day").toISOString();
  }
  // Identical fixed-width formats ⇒ lexicographic min = chronological min of
  // the wall-clock digits, which is the right axis for a tz-naive column.
  return localStartsOfToday(venueTimezones)
    .map((d) => d.format("YYYY-MM-DDTHH:mm:ss"))
    .sort()[0];
}

/**
 * Instant form, for comparing against UTC-rendered datetimes — Tixologi's
 * `ev.start_date` carries a real offset, so the boundary must be an instant,
 * not naive digits (see `getVenueShowGroupData`). Same anchoring and fallback
 * rules as {@link upcomingShowsCutoff}.
 */
export function upcomingShowsCutoffInstant(
  venueTimezones: VenuePageTimezones
): Dayjs {
  if (!venueTimezones?.length) {
    return dayjs().subtract(1, "day");
  }
  return localStartsOfToday(venueTimezones).reduce((min, d) =>
    d.isBefore(min) ? d : min
  );
}
